import { Label } from "design-system/atoms/Label"
import { renderLiquid } from "lopermut"
import { WhatsappTemplatePreview } from "modules/campaigns/components/Sections/channels/whatsapp/WhatsappTemplatePreview"
import Locale from "modules/shared/components/Miscelanea/Locale"
import { useVaultPreviewContext } from "modules/shared/hooks/useVaultPreviewContext"
import { useEffect } from "react"
import { useTranslation } from "react-i18next"
import {
  WorkflowActionInConversationMessageConfigSchema,
  WorkflowNodeSchema,
  ChannelEnum,
} from "schema"
import z from "zod"
import { useWorkflowBuilder } from "../../hooks/useWorkflowBuilder"
import { useEventPayload } from "../../hooks/useEventPayload"
import { EditInConversationMessagePayloadDialog } from "./EditInConversationMessagePayloadDialog"

type SendInConversationMessageProps = {
  nodeIndex: number
  node: Extract<z.infer<typeof WorkflowNodeSchema>, { type: "Action" }>
}

export function SendInConversationMessage({ nodeIndex, node }: SendInConversationMessageProps) {
  const { t } = useTranslation()
  const { form } = useWorkflowBuilder()

  const { eventPayload, eventName } = useEventPayload({ nodeId: node.id })
  const previewContext = useVaultPreviewContext(eventPayload ?? {})

  // Validate and initialize config using zod
  useEffect(() => {
    const result = WorkflowActionInConversationMessageConfigSchema.safeParse(node.config)

    if (!result.success) {
      form.setValue(`nodes.${nodeIndex}.config`, {
        message: "",
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Watch form values for real-time updates
  const message = form.watch(`nodes.${nodeIndex}.config.message`)
  const locale = form.watch(`nodes.${nodeIndex}.config.locale`)
  // `channel` only exists on the campaign form; the workflow form has no channel (no WhatsApp
  // template preview), so read it defensively.
  const channel = (form.watch() as { channel?: (typeof ChannelEnum)[keyof typeof ChannelEnum] })
    .channel

  if (!eventName || !eventPayload) {
    return (
      <span className="text-sm text-muted-foreground">
        {t("campaigns.action.availableActions.SendInConversationMessage.noEventSelected")}
      </span>
    )
  }

  const messageWithLiquid = renderLiquid(message ?? "null", previewContext)

  return (
    <div className="flex flex-col items-start gap-4">
      <div className="flex w-full flex-wrap items-center gap-2">
        <Label>
          {t("campaigns.action.availableActions.SendInConversationMessage.config.message")}
        </Label>
        {locale && (
          <div
            className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground"
            aria-label={t(
              "campaigns.action.availableActions.SendInConversationMessage.config.locale",
            )}
          >
            <Locale code={locale} />
          </div>
        )}
      </div>
      {channel === ChannelEnum.Whatsapp ? (
        <WhatsappTemplatePreview body={messageWithLiquid} />
      ) : (
        <div className="w-full whitespace-pre-wrap wrap-break-word text-base leading-relaxed">
          {messageWithLiquid}
        </div>
      )}
      <EditInConversationMessagePayloadDialog
        initialPayload={message}
        onChangePayload={(config) => {
          form.setValue(`nodes.${nodeIndex}.config`, config, {
            shouldDirty: true,
          })
          form.trigger(`nodes.${nodeIndex}.config`)
        }}
        eventPayload={eventPayload}
        eventName={eventName}
        nodeIndex={nodeIndex}
        channel={channel}
      />
    </div>
  )
}
